import { FunctionComponent } from 'react'
import { Clock, Download, Link2Off, UserStar } from 'lucide-react'
import Tag from '../components/Tag'
import { styles } from '../styles/themes/style'
import { ITag } from '../interfaces/models/connection'

interface IAppTagProps {
    tag: ITag
    $width?: string
    className?: string
    onDestructiveClickHandler?: (e: any) => void
    tooltipMessage?: string
 }

const AUTO_TAG_TITLES = ['LinkedIn', 'LinkedIn 1st', 'Imported', 'New Connection', 'CSV', 'Disconnected', 'Prospect', 'Removal Scheduled']
// Lucide strokes are in 24×24 viewBox units; at 12px render size, strokeWidth 3 ≈ 1.5px on screen.
const LUCIDE_TAG_ICON_PROPS = { size: 12, strokeWidth: 3 }

/** Pipeline tag kanban bars: 6px / 4px / 8px at 12px icon (top-aligned, 24 viewBox lengths 12 / 8 / 16). */
const TagKanbanIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M5 3v12" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
        <path d="M12 3v8" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
        <path d="M19 3v16" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
    </svg>
)

const getTagTint = (color?: string) => {
    if (!color) return styles.colors.white

    const normalized = color.trim().replace('#', '')
    const hex = normalized.length === 3
        ? normalized.split('').map(char => `${char}${char}`).join('')
        : normalized

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return styles.colors.white

    const amount = 0.92
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    const mix = (channel: number) => Math.round(channel * (1 - amount) + 255 * amount)
        .toString(16)
        .padStart(2, '0')

    return `#${mix(r)}${mix(g)}${mix(b)}`
}

const AppTag: FunctionComponent<IAppTagProps> = ({ tag, $width, className, onDestructiveClickHandler, tooltipMessage }) => {

    let SVGtype = ''
    let icon: JSX.Element | undefined
    const emoji = undefined

    if (['LinkedIn', 'LinkedIn 1st'].includes(tag.title)) SVGtype = 'linkedin'
    else if (['Imported'].includes(tag.title)) icon = <Download {...LUCIDE_TAG_ICON_PROPS} />
    else if (['New Connection'].includes(tag.title)) icon = <UserStar {...LUCIDE_TAG_ICON_PROPS} />
    else if (['CSV'].includes(tag.title)) SVGtype = ""
    else if (['Disconnected'].includes(tag.title)) icon = <Link2Off {...LUCIDE_TAG_ICON_PROPS} />
    else if (['Prospect'].includes(tag.title)) SVGtype = "leadDelta"
    else if (['Removal Scheduled'].includes(tag.title)) icon = <Clock {...LUCIDE_TAG_ICON_PROPS} />
    else if (tag.forKanban) {
        icon = <TagKanbanIcon />
    }
    else if (!tag.private) SVGtype = "tagCompany"
    else SVGtype = ""

    const displayTitle = tag.title === 'CSV'
        ? 'csv'
        : tag.title === 'Disconnected'
            ? (tag?.removedFromLD === false ? 'Li Removed' : 'LD Removed')
            : tag.title

    // Public/company tags should keep user-picked color (tint + outline), same as custom tags.
    const isSystemTag = tag.isForbidden || AUTO_TAG_TITLES.includes(tag.title)
    const tagColor = tag.color || styles.colors.primary600
    const backgroundColor = tag.forKanban
        ? getTagTint(tagColor)
        : isSystemTag
            ? styles.colors.white
            : getTagTint(tagColor)
    const borderColor = tag.forKanban || !isSystemTag
        ? tagColor
        : styles.colors.black200
    
    // Determine tooltip text - for kanban tags show "[emoji] Pipeline name: Column name"
    const defaultTooltipText = tag.forKanban && tag.kanbanName 
        ? `${tag.kanbanName}: ${tag.title}` 
        : undefined; // Use default tooltip for non-kanban tags

    const finalTooltipText = tooltipMessage ?? defaultTooltipText;

    return  <Tag 
        className={className}
        onDestructiveClickHandler={onDestructiveClickHandler}
        $backgroundColor={backgroundColor}
        $borderColor={borderColor} 
        $color={tagColor} 
        title={displayTitle} 
        tooltipMessage={finalTooltipText}
        SVGtype={SVGtype}
        icon={icon}
        emoji={emoji}
        $width={$width}
        />
}

export default AppTag
